/**
 * main-product-extras.js
 * Encapsula: animación de botón, urgencia de inventario, sticky cart mobile
 * y carrusel "Combina con". Auto-init por IntersectionObserver, scoped a `this`.
 */
if (!customElements.get('main-product-extras')) {
  class MainProductExtras extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.threshold = parseInt(this.dataset.invThreshold, 10) || 30;

      // IMPORTANTE: este elemento usa `display: contents` en CSS para no
      // romper el grid de la sección. Eso significa que no tiene caja de
      // layout propia, así que IntersectionObserver nunca lo detecta como
      // intersectando si se observa `this` directamente. Observamos un
      // hijo real (.page-width) que sí tiene dimensiones.
      const target = this.querySelector('.page-width') || this;

      this._io = new IntersectionObserver(
        (entries, obs) => {
          if (!entries[0].isIntersecting) return;
          obs.disconnect();
          this.init();
        },
        { rootMargin: '300px' }
      );
      this._io.observe(target);

      // Red de seguridad: si por lo que sea el observer nunca dispara
      // (cambios de layout, navegadores raros, etc.), inicializa de todos
      // modos tras cargar, para no dejar el botón de carrito o el carrusel
      // muertos silenciosamente.
      this._fallbackTimer = setTimeout(() => {
        if (!this._initialized) this.init();
      }, 4000);
    }

    disconnectedCallback() {
      this._io?.disconnect();
      clearTimeout(this._fallbackTimer);
      this._scrollHandler && window.removeEventListener('scroll', this._scrollHandler);
      this._resizeHandler && window.removeEventListener('resize', this._resizeHandler);
      this._combinaObserver && this._combinaObserver.disconnect();
      this._galleryDotsObserver && this._galleryDotsObserver.disconnect();
    }

    init() {
      if (this._initialized) return;
      this._initialized = true;
      clearTimeout(this._fallbackTimer);

      this.initBounce();
      this.initUrgency();
      this.initStickyCart();
      this.initCombina();
      this.initGalleryDots();
    }

    /* ── Animación de salto en botón añadir al carrito ── */
    initBounce() {
      const addBtn = this.querySelector('.product-form__submit:not([disabled])');
      if (!addBtn) return;

      const bounce = () => {
        if (addBtn.disabled) return;
        addBtn.classList.add('fs-bounce');
      };
      addBtn.addEventListener(
        'animationend',
        () => addBtn.classList.remove('fs-bounce'),
        { passive: true }
      );
      setTimeout(bounce, 800);
      addBtn.addEventListener('mouseenter', bounce, { passive: true });

      this._addBtn = addBtn;
    }

    /* ── Inventario urgencia ── */
    initUrgency() {
      const sid = this.sectionId;
      const urgencyEl = document.getElementById('fsInventoryUrgency-' + sid);
      if (!urgencyEl) return;
      const urgencyCount = document.getElementById('fsInventoryCount-' + sid);
      const barFill = document.getElementById('fsInventoryBar-' + sid);
      const threshold = this.threshold;

      const update = (qty, mgmt, policy) => {
        if (mgmt !== 'shopify' || policy === 'continue' || qty > threshold || qty <= 0) {
          urgencyEl.style.display = 'none';
          return;
        }
        urgencyEl.style.display = '';
        if (urgencyCount) urgencyCount.textContent = qty;
        if (barFill) {
          const pct = Math.round(Math.min(100, (qty / threshold) * 100));
          barFill.style.width = pct + '%';
        }
      };

      update(
        parseInt(this.dataset.invQty, 10) || 0,
        this.dataset.invMgmt || '',
        this.dataset.invPolicy || ''
      );

      document.addEventListener('variant:change', (e) => {
        if (!e.detail || !e.detail.variant) return;
        const v = e.detail.variant;
        update(v.inventory_quantity || 0, v.inventory_management || '', v.inventory_policy || '');
        if (this._addBtn && !this._addBtn.disabled) {
          setTimeout(() => this._addBtn.classList.add('fs-bounce'), 150);
        }
      });
    }

    /* ── Sticky cart mobile ── */
    initStickyCart() {
      const sid = this.sectionId;
      const sticky = document.getElementById('fsStickyCart-' + sid);
      const stickyBtn = document.getElementById('fsStickyBtn-' + sid);
      const stickyVariant = document.getElementById('fsStickyVariant-' + sid);
      const mainForm = document.getElementById('product-form-' + sid);
      const mainBtn = mainForm ? mainForm.querySelector('[name="add"]') : null;

      if (!sticky || !stickyBtn || !mainBtn) return;

      const checkVisibility = () => {
        const rect = mainBtn.getBoundingClientRect();
        const isVisible = rect.top >= 0 && rect.bottom <= window.innerHeight;
        sticky.hidden = isVisible;
        sticky.setAttribute('aria-hidden', isVisible ? 'true' : 'false');
      };

      this._scrollHandler = checkVisibility;
      window.addEventListener('scroll', checkVisibility, { passive: true });
      checkVisibility();

      if (stickyVariant) {
        const mainVariantSelect = mainForm.querySelector('select[name="id"]');
        stickyVariant.addEventListener('change', () => {
          if (!mainVariantSelect) return;
          mainVariantSelect.value = stickyVariant.value;
          mainVariantSelect.dispatchEvent(new Event('change', { bubbles: true }));
        });
        if (mainVariantSelect) {
          mainVariantSelect.addEventListener('change', () => {
            stickyVariant.value = mainVariantSelect.value;
          });
        }
      }

      stickyBtn.addEventListener('click', () => mainBtn.click());
    }

    /* ── Carrusel "Combina con" ── */
    initCombina() {
      const checkIconSVG =
        '<svg viewBox="0 0 24 24" aria-hidden="true"><polyline points="20 6 9 12 4 10" stroke-linecap="round" stroke-linejoin="round"/></svg>';

      const addToCart = (variantId, btn) => {
        if (!variantId || btn.disabled) return;
        const originalHTML = btn.innerHTML;
        btn.classList.add('is-loading');
        btn.disabled = true;
        btn.innerHTML = '...';

        fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify({ id: variantId, quantity: 1 }),
        })
          .then((r) => r.json().then((data) => ({ ok: r.ok, data })))
          .then((result) => {
            btn.classList.remove('is-loading');
            if (!result.ok || result.data.status) {
              btn.disabled = false;
              btn.innerHTML = originalHTML;
              return;
            }
            btn.classList.add('is-added');
            btn.innerHTML = checkIconSVG + ' ¡Agregado!';

            const cartDrawerEl = document.querySelector('cart-drawer');
            const cartNotificationEl = document.querySelector('cart-notification');
            const sectionIds = [
              'cart-drawer',
              'cart-icon-bubble',
              'cart-live-region-text',
              'cart-notification-product',
              'cart-notification-button',
            ];

            fetch('/?sections=' + sectionIds.join(','))
              .then((r) => r.json())
              .then((sections) => {
                if (sections['cart-icon-bubble']) {
                  const bubbleWrap = document.getElementById('cart-icon-bubble');
                  if (bubbleWrap) {
                    const temp = document.createElement('div');
                    temp.innerHTML = sections['cart-icon-bubble'];
                    const newBubble = temp.querySelector('.shopify-section')
                      ? temp.querySelector('.shopify-section').innerHTML
                      : temp.innerHTML;
                    bubbleWrap.innerHTML = newBubble;
                  }
                }

                if (cartDrawerEl && sections['cart-drawer']) {
                  const temp = document.createElement('div');
                  temp.innerHTML = sections['cart-drawer'];
                  const newDrawerInner = temp.querySelector('cart-drawer');

                  /*
                   * FIX BUG REAL "carrito congelado/cortado cuando estaba vacío":
                   *
                   * Antes solo copiábamos newDrawerInner.innerHTML, pero
                   * NUNCA sincronizábamos las clases del propio elemento
                   * <cart-drawer> (el contenedor externo). Dawn usa la
                   * clase "is-empty" en ESE elemento para decidir con CSS
                   * si oculta el header, fuerza un grid de una sola fila
                   * centrada, etc. (ej: `cart-drawer.is-empty .drawer__header
                   * { display: none }`).
                   *
                   * Cuando el carrito pasaba de 0 → 1 producto, el HTML
                   * interno SÍ se actualizaba correctamente (ya no es el
                   * estado vacío), pero la clase "is-empty" se quedaba
                   * pegada en el <cart-drawer> viejo porque nunca la
                   * tocábamos. Resultado: CSS seguía ocultando el header y
                   * forzando el layout de "vacío" sobre contenido que ya
                   * no lo era — de ahí el drawer "cortado/congelado".
                   *
                   * Cuando el carrito ya tenía productos antes, ambos
                   * estados (viejo y nuevo) NO tienen la clase is-empty,
                   * así que no había desincronización y por eso ahí nunca
                   * fallaba.
                   *
                   * Fix: copiar también el className completo del nuevo
                   * <cart-drawer> sobre el real.
                   */
                  if (newDrawerInner) {
                    cartDrawerEl.className = newDrawerInner.className;
                    cartDrawerEl.innerHTML = newDrawerInner.innerHTML;
                  }

                  requestAnimationFrame(() => {
                    requestAnimationFrame(() => {
                      if (typeof cartDrawerEl.open === 'function') {
                        cartDrawerEl.open();
                      } else {
                        cartDrawerEl.classList.remove('drawer');
                        cartDrawerEl.removeAttribute('hidden');
                      }
                    });
                  });
                } else if (cartNotificationEl && typeof cartNotificationEl.renderContents === 'function') {
                  cartNotificationEl.renderContents(result.data);
                } else {
                  window.location.href = '/cart';
                }
              })
              .catch(() => {
                window.location.href = '/cart';
              });

            document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));

            setTimeout(() => {
              btn.classList.remove('is-added');
              btn.disabled = false;
              btn.innerHTML = originalHTML;
            }, 2500);
          })
          .catch(() => {
            btn.classList.remove('is-loading');
            btn.disabled = false;
            btn.innerHTML = originalHTML;
          });
      };

      const initOne = (combinaEl) => {
        if (combinaEl.dataset.fsInit) return;
        combinaEl.dataset.fsInit = '1';

        const track = combinaEl.querySelector('.fs-combina__track');
        const dotsEl = combinaEl.querySelector('.fs-combina__dots');
        const prevBtn = combinaEl.querySelector('.fs-combina__arrow--prev');
        const nextBtn = combinaEl.querySelector('.fs-combina__arrow--next');
        if (!track) return;

        const cardEls = track.querySelectorAll('.fs-combina-card');
        const total = cardEls.length;
        if (!total) return;
        let currentIdx = 0;
        const getVisible = () => 2;
        const maxIdx = () => Math.max(0, total - getVisible());
        const getCardWidth = () =>
          cardEls[0] ? cardEls[0].getBoundingClientRect().width + 12 : 0;

        const updateDots = () => {
          if (!dotsEl) return;
          const activePage = Math.floor(currentIdx / getVisible());
          dotsEl
            .querySelectorAll('.fs-combina__dot')
            .forEach((d, i) => d.classList.toggle('is-active', i === activePage));
        };

        const goTo = (idx) => {
          const max = maxIdx();
          currentIdx = Math.max(0, Math.min(idx, max));
          track.style.transform = 'translateX(-' + currentIdx * getCardWidth() + 'px)';
          if (prevBtn) prevBtn.disabled = currentIdx === 0;
          if (nextBtn) nextBtn.disabled = currentIdx >= max;
          updateDots();
        };

        const buildDots = () => {
          if (!dotsEl) return;
          const pages = Math.ceil(total / getVisible());
          dotsEl.innerHTML = '';
          if (pages <= 1) return;
          for (let i = 0; i < pages; i++) {
            const dot = document.createElement('button');
            dot.className = 'fs-combina__dot' + (i === 0 ? ' is-active' : '');
            dot.setAttribute('aria-label', 'Página ' + (i + 1));
            dot.addEventListener('click', () => goTo(i * getVisible()));
            dotsEl.appendChild(dot);
          }
        };

        buildDots();
        if (prevBtn) prevBtn.addEventListener('click', () => goTo(currentIdx - 1));
        if (nextBtn) nextBtn.addEventListener('click', () => goTo(currentIdx + 1));

        let touchStartX = 0;
        track.addEventListener('touchstart', (e) => (touchStartX = e.touches[0].clientX), {
          passive: true,
        });
        track.addEventListener(
          'touchend',
          (e) => {
            const diff = touchStartX - e.changedTouches[0].clientX;
            if (Math.abs(diff) > 40) goTo(diff > 0 ? currentIdx + 1 : currentIdx - 1);
          },
          { passive: true }
        );

        let resizeTimer;
        this._resizeHandler = () => {
          clearTimeout(resizeTimer);
          resizeTimer = setTimeout(() => {
            buildDots();
            goTo(0);
          }, 200);
        };
        window.addEventListener('resize', this._resizeHandler, { passive: true });

        goTo(0);

        track.addEventListener('click', (e) => {
          const btn = e.target.closest('.fs-combina-card__btn');
          if (!btn) return;
          e.preventDefault();
          addToCart(btn.getAttribute('data-variant-id'), btn);
        });
      };

      this.querySelectorAll('.fs-combina').forEach(initOne);

      const wrapper = this.querySelector('product-recommendations.fs-combina-wrapper');
      if (wrapper) {
        const observer = new MutationObserver((mutations) => {
          mutations.forEach((m) => {
            m.addedNodes.forEach((node) => {
              if (node.nodeType === 1 && node.classList && node.classList.contains('fs-combina')) {
                initOne(node);
              }
            });
          });
        });
        observer.observe(wrapper, { childList: true });
        this._combinaObserver = observer;
      }

      document.addEventListener('shopify:section:load', () => {
        this.querySelectorAll('.fs-combina').forEach(initOne);
      });
    }

    /* ── Dots de la galería de imágenes del producto ──
       Reemplaza visualmente el contador "1/N" + flechas nativos de Dawn
       (slider-component dentro de product-media-gallery.liquid) por puntos,
       sin tocar ese snippet. Solo se oculta su contador por CSS y se
       sincronizan dots nuevos con la slide activa real del slider nativo. */
    initGalleryDots() {
      const sid = this.sectionId;
      const gallery = document.getElementById('MediaGallery-' + sid);
      if (!gallery) return;

      const slider = gallery.querySelector('#GalleryViewer-' + sid);
      const slideList = gallery.querySelector('#Slider-Gallery-' + sid);
      const counterWrap = gallery.querySelector('.slider-buttons');
      if (!slider || !slideList || !counterWrap) return;

      let slides = Array.from(slideList.querySelectorAll('.product__media-item'));
      if (slides.length <= 1) return; // una sola imagen: nada que paginar

      // Oculta el contador/flechas nativos de Dawn solo en esta galería
      // (regla CSS scoped en main-product-custom.css, no afecta otras secciones).
      counterWrap.classList.add('fs-gallery-counter--replaced');

      let dotsWrap = gallery.querySelector('.fs-gallery-dots');
      if (!dotsWrap) {
        dotsWrap = document.createElement('div');
        dotsWrap.className = 'fs-gallery-dots';
        dotsWrap.setAttribute('role', 'tablist');
        dotsWrap.setAttribute('aria-label', 'Selector de imagen');
        slider.insertAdjacentElement('afterend', dotsWrap);
      }

      const buildDots = () => {
        dotsWrap.innerHTML = '';
        slides.forEach((slideEl, i) => {
          const dot = document.createElement('button');
          dot.type = 'button';
          dot.className = 'fs-gallery-dot' + (i === 0 ? ' is-active' : '');
          dot.setAttribute('role', 'tab');
          dot.setAttribute('aria-label', 'Imagen ' + (i + 1) + ' de ' + slides.length);
          dot.addEventListener('click', () => goToSlide(slideEl, i));
          dotsWrap.appendChild(dot);
        });
      };

      // Navega usando el mismo mecanismo que Dawn ya usa internamente:
      // las miniaturas (#Thumbnail-{sid}-N) están conectadas via
      // aria-controls al slider-component real, así que un click ahí
      // sí actualiza la slide activa, el scroll del track Y dispara
      // los eventos internos del tema. scrollIntoView() por sí solo
      // mueve la vista pero no pasa por esa lógica.
      let recentClickIndex = null;
      let recentClickTimer = null;

      const goToSlide = (slideEl, index) => {
        const thumbnailBtn = gallery.querySelector('#Thumbnail-' + sid + '-' + index);
        if (thumbnailBtn) {
          thumbnailBtn.click();
        } else {
          // Fallback si no hay miniaturas habilitadas en este layout.
          slideEl.scrollIntoView({ behavior: 'smooth', inline: 'start', block: 'nearest' });
        }

        // Marca el dot ya mismo (sensación instantánea) y protege ese
        // estado por una ventana corta: el slider nativo de Dawn tarda
        // un poco en terminar su scroll/transición y en marcar la nueva
        // slide como is-active. Sin esta ventana, el MutationObserver
        // de abajo lee el DOM a mitad de esa transición, ve que la
        // slide vieja todavía es is-active, y revierte el dot — por
        // eso se quedaba "a medias" en vez de completar el cambio.
        setActiveDot(index);
        recentClickIndex = index;
        clearTimeout(recentClickTimer);
        recentClickTimer = setTimeout(() => {
          recentClickIndex = null;
        }, 400);
      };

      const setActiveDot = (index) => {
        dotsWrap
          .querySelectorAll('.fs-gallery-dot')
          .forEach((d, i) => d.classList.toggle('is-active', i === index));
      };

      const updateActive = () => {
        // Mientras estemos dentro de la ventana de un click propio,
        // confía en ese índice en vez de releer un DOM que aún puede
        // estar a mitad de transición.
        if (recentClickIndex !== null) {
          setActiveDot(recentClickIndex);
          return;
        }
        const current = slideList.querySelector('.product__media-item.is-active') || slides[0];
        const idx = slides.indexOf(current);
        if (idx >= 0) setActiveDot(idx);
      };

      buildDots();
      updateActive();

      // slider-component (global.js) marca la slide activa con la clase
      // `is-active` y dispara este evento cada vez que cambia. Nos
      // sincronizamos con eso en vez de reimplementar el tracking de scroll.
      slider.addEventListener('slideChanged', updateActive);

      // Red de seguridad: si el tema regenera la galería vía AJAX
      // (cambio de variante con imágenes distintas), reconstruye los dots.
      const mo = new MutationObserver(() => {
        const newSlides = Array.from(slideList.querySelectorAll('.product__media-item'));
        if (newSlides.length !== slides.length) {
          slides = newSlides;
          buildDots();
        }
        updateActive();
      });
      mo.observe(slideList, { childList: true, attributes: true, attributeFilter: ['class'] });
      this._galleryDotsObserver = mo;
    }
  }

  customElements.define('main-product-extras', MainProductExtras);
}